// src/features/services/sales/api/skuCatalogApi.ts
// ─────────────────────────────────────────────────────────────────────────────
// SKU Catalog — FE API layer.
//
// Source of truth: scope-entity system (namespace "sku.definition").
// All calls go through the BFF; zero orchestrator internals leak here.
//
// The SKU entity family supports flexible attributes via BFF-owned type maps.
// Adding new attributes (IoT defaults, hardware specs, pricing) requires zero
// schema changes — extend SKU_TYPE_MAP in the BFF service layer.
// ─────────────────────────────────────────────────────────────────────────────
import { bff } from '@/lib/http/bff';

const BASE = '/sku-catalog';

// ── Types ────────────────────────────────────────────────────────────────────

/** Hardware specification embedded in a SKU definition. */
export interface HardwareSpec {
  mcu?: string;         // e.g. "esp32-s3"
  flash?: string;       // e.g. "4MB"
  sensors?: string[];   // e.g. ["sht30"]
  [key: string]: unknown;
}

/** Full SKU definition — FE-ready shape produced by BFF digester. */
export interface SkuDefinition {
  sku: string;
  id_scope: number | null;
  label: string;
  description: string;
  category: string;        // iot | pacdaemon | retail | accessory | ...
  sku_type: string;        // component | assembly (default: assembly)
  tier: number;            // BOM hierarchy level: 0 = assembly, 1+ = component depth
  parent_sku: string | null; // Parent assembly SKU (null for top-level)
  version: string;         // Semantic version of the definition
  active: boolean;
  icon: string | null;
  tags: string[];

  // IoT device template defaults
  publish_period_ms: number | null;
  sensor_interval_ms: number | null;
  sensor_enabled: boolean | null;
  config_poll_ms: number | null;
  log_flush_ms: number | null;
  wifi_enabled: boolean | null;

  // Structured specs
  hardware_spec: HardwareSpec | null;
  default_config: Record<string, unknown> | null;
  required_fields: string[] | null;

  // Business
  unit_cost: number | null;
  currency: string | null;
  station: string | null;

  // Audit
  created_by: string | null;
  created_at: string | null;
  updated_at: string | null;
}

/** Payload for creating/updating a SKU definition. */
export interface UpsertSkuPayload {
  sku: string;
  label?: string;
  description?: string;
  category?: string;
  sku_type?: string;
  tier?: number;
  parent_sku?: string | null;
  version?: string;
  active?: boolean;
  icon?: string;
  tags?: string[];

  // IoT defaults (template values for new devices/orders)
  publish_period_ms?: number;
  sensor_interval_ms?: number;
  sensor_enabled?: boolean;
  config_poll_ms?: number;
  log_flush_ms?: number;
  wifi_enabled?: boolean;

  // Structured specs
  hardware_spec?: HardwareSpec;
  default_config?: Record<string, unknown>;
  required_fields?: string[];

  // Business
  unit_cost?: number;
  currency?: string;
  station?: string | null;

  // Food / hospitality (TableOps v2)
  menu_description?: string;
  prep_time_min?: number;
  allergens?: string[];
  dietary_flags?: string[];
  serving_size?: string;
  calories_kcal?: number;
  display_image?: string;
}

/** IoT config defaults extracted from a SKU. */
export interface SkuDefaults {
  sku: string;
  publish_period_ms: number;
  sensor_interval_ms: number;
  sensor_enabled: boolean;
  config_poll_ms: number;
  log_flush_ms: number;
  wifi_enabled: boolean;
  default_config: Record<string, unknown> | null;
  required_fields: string[];
  hardware_spec: HardwareSpec | null;
}

export interface SkuListResponse {
  items: SkuDefinition[];
  next_cursor: string | null;
}

// ── Query keys ───────────────────────────────────────────────────────────────

export const SKU_KEYS = {
  all:      ['sku-catalog'] as const,
  list:     (params?: Record<string, unknown>) => ['sku-catalog', 'list', params] as const,
  detail:   (sku: string) => ['sku-catalog', 'detail', sku] as const,
  defaults: (sku: string) => ['sku-catalog', 'defaults', sku] as const,
};

// ── API functions ────────────────────────────────────────────────────────────

export async function upsertSkuApi(payload: UpsertSkuPayload): Promise<SkuDefinition> {
  return bff.post<SkuDefinition, UpsertSkuPayload>(BASE, payload);
}

export async function fetchSkuListApi(
  params?: { category?: string; active?: string; limit?: number; cursor?: string },
): Promise<SkuListResponse> {
  return bff.get<SkuListResponse>(BASE, params as any);
}

export async function fetchSkuApi(sku: string): Promise<SkuDefinition> {
  return bff.get<SkuDefinition>(`${BASE}/${encodeURIComponent(sku)}`);
}

export async function fetchSkuDefaultsApi(sku: string): Promise<SkuDefaults> {
  return bff.get<SkuDefaults>(`${BASE}/${encodeURIComponent(sku)}/defaults`);
}

export async function deactivateSkuApi(sku: string): Promise<SkuDefinition> {
  return bff.post<SkuDefinition>(`${BASE}/${encodeURIComponent(sku)}/deactivate`);
}

export async function reactivateSkuApi(sku: string): Promise<SkuDefinition> {
  return bff.post<SkuDefinition>(`${BASE}/${encodeURIComponent(sku)}/reactivate`);
}
